import React from "react";

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Fade,
    SvgIconProps,
    Typography,
    useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

import { useSelector } from "react-redux";

const FadeTransition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>,
) {
    return <Fade ref={ref} {...props} />;
});

type ConfirmColor = "error" | "warning" | "primary" | "secondary" | "success";

const ICON_BY_COLOR: Record<ConfirmColor, React.ComponentType<SvgIconProps>> = {
    error: ErrorOutlineRoundedIcon,
    warning: WarningAmberRoundedIcon,
    success: CheckCircleOutlineRoundedIcon,
    primary: WarningAmberRoundedIcon,
    secondary: WarningAmberRoundedIcon,
};

interface ConfirmationModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    confirmColor?: ConfirmColor;
    isLoading?: boolean;
    hideCancelButton?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    open,
    onClose,
    onConfirm,
    title = "Are you sure?",
    message = "This action cannot be undone.",
    confirmText,
    cancelText,
    confirmColor = "error",
    isLoading = false,
    hideCancelButton = false,
}) => {
    const theme = useTheme();
    const language = useSelector(
        (state: any) => state?.languagesState?.languages,
    );

    const StatusIcon = ICON_BY_COLOR[confirmColor] ?? ICON_BY_COLOR.primary;

    const resolvedConfirmText =
        confirmText ?? language?.delete_swal_modal?.yes ?? "Yes";
    const resolvedCancelText =
        cancelText ?? language?.delete_swal_modal?.no ?? "No";

    return (
        <Dialog
            data-cy="confirmation-modal"
            open={open}
            onClose={!isLoading ? onClose : undefined}
            maxWidth="xs"
            fullWidth
            TransitionComponent={FadeTransition}
            TransitionProps={{ timeout: 250 }}
            BackdropProps={{
                sx: {
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    backdropFilter: "blur(2px)",
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: "0 24px 48px rgba(0, 0, 0, 0.15)",
                    overflow: "visible",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pt: 3,
                }}
            >
                <Box
                    sx={{
                        width: 120,
                        height: 120,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <StatusIcon
                        sx={{
                            fontSize: 96,
                            color: theme.palette[confirmColor].main,
                        }}
                    />
                </Box>
            </Box>

            <DialogContent sx={{ px: 3, pb: 2 }}>
                <Typography
                    variant="h6"
                    fontSize={26}
                    component="div"
                    textAlign="center"
                    sx={{ mb: 1 }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                    }}
                >
                    {message}
                </Typography>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 3,
                    gap: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                }}
            >
                {!hideCancelButton && (
                    <Button
                        data-cy="confirmation-cancel"
                        variant="outlined"
                        color="inherit"
                        onClick={onClose}
                        disabled={isLoading}
                        sx={{
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.secondary,
                            "&:hover": {
                                borderColor: theme.palette.text.secondary,
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                    >
                        {resolvedCancelText}
                    </Button>
                )}
                <Button
                    data-cy="confirmation-confirm"
                    variant="contained"
                    color={confirmColor}
                    onClick={onConfirm}
                    disabled={isLoading}
                    startIcon={
                        isLoading ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : null
                    }
                >
                    {resolvedConfirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationModal;
